// src/components/trial/lp/trial-beta/sections/how-it-works.tsx
// Ported from the design attachment, markup lines 3855-3955 (the "p6" redesign of this section).
//
// One departure from a literal port: the source's `<h1 id="p6-title">` (3860) renders as `<h2>`
// here, matching multiconnection.tsx/multi-internet.tsx's own rule — hero.tsx owns this page's
// only `<h1>`. Tag only; DOM depth and every other attribute are untouched.
//
// The step-1 "DOWNLOAD FOR WINDOWS" button is NOT a download: its own `aria-label` (3872) says
// "…and replay the download animation", and ../animation/how-it-works.ts wires its click to
// rewind the demo's clock, nothing else. The page's real download route is the header nav
// (`/download`, spec D-6) — this button never gets an `href` or a `download` attribute.
//
// Copy split matches multi-internet.tsx: this is the section's static, no-JS resting state,
// verbatim from the source (down to which game is selected by default and what status text each
// badge starts on) via `t(...)`. ../animation/how-it-works.ts overwrites all of it from `strings`
// the moment the section scrolls into view.
import { useTranslation } from 'next-i18next';

import { howItWorksAnimation } from '../animation/how-it-works';
import { buildHowItWorksHudStrings } from '../animation/hud-i18n';
import { useSectionAnimation } from '../animation/use-section-animation';
import { TRIAL_BETA_ASSETS } from '../assets';

export function HowItWorks() {
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const sectionRef = useSectionAnimation(howItWorksAnimation, buildHowItWorksHudStrings(t));

  return (
    <section className='p6' id='how-it-works' ref={sectionRef} aria-labelledby='p6-title'>
      <div className='p6__ambient' aria-hidden='true' />
      <div className='p6__shell'>
        <header className='p6__header'>
          <p>{t('howItWorks.eyebrow')}</p>
          <h2 id='p6-title'>
            {t('howItWorks.title')} <span>{t('howItWorks.titleHighlight')}</span>
          </h2>
          <div className='p6__header-chip'>
            <i /> {t('howItWorks.headerChip')}
          </div>
        </header>

        <div className='p6__workflow'>
          <article className='p6-card p6-card--download' data-step='download'>
            <div className='p6-card__number'>
              <b>{t('howItWorks.download.number')}</b>
              <span>{t('howItWorks.download.label')}</span>
            </div>
            <div className='p6-card__stage p6-download'>
              <div className='p6-download__launch'>
                <span className='p6-download__kicker'>{t('howItWorks.download.kicker')}</span>
                <button className='p6-download__button' type='button' aria-label={t('howItWorks.download.buttonAriaLabel')}>
                  <span className='p6-windows' aria-hidden='true'>
                    <i />
                    <i />
                    <i />
                    <i />
                  </span>
                  <span>
                    <small>{t('howItWorks.download.buttonSmall')}</small>
                    <b>{t('howItWorks.download.buttonStrong')}</b>
                  </span>
                </button>
                <span className='p6-download__note'>{t('howItWorks.download.note')}</span>
              </div>
              <span className='p6-cursor' aria-hidden='true'>
                <i />
              </span>

              <div className='p6-installer' aria-hidden='true'>
                <div className='p6-installer__bar'>
                  <i />
                  <i />
                  <i />
                  <span>{t('howItWorks.download.installerFile')}</span>
                </div>
                <div className='p6-installer__icon'>
                  <svg viewBox='0 0 32 32' aria-hidden='true'>
                    <path className='p6-download-arrow' d='M16 5v14M10.5 14.5 16 20l5.5-5.5' />
                    <path className='p6-download-tray' d='M7 25h18' />
                  </svg>
                </div>
                <span className='p6-installer__label'>{t('howItWorks.download.installerLabel')}</span>
                <b className='p6-installer__percent' data-download-percent>
                  0%
                </b>
                <div className='p6-installer__track'>
                  <i />
                </div>
              </div>
            </div>
            <h2>{t('howItWorks.download.title')}</h2>
            <p>{t('howItWorks.download.description')}</p>
            <div className='p6-card__status'>
              <i />
              <span data-download-status>{t('howItWorks.download.defaultStatus')}</span>
            </div>
          </article>

          <div className='p6-connector' aria-hidden='true'>
            <i />
            <b />
            <i />
          </div>

          <article className='p6-card p6-card--games' data-step='games'>
            <div className='p6-card__number'>
              <b>{t('howItWorks.games.number')}</b>
              <span>{t('howItWorks.games.label')}</span>
            </div>
            <div className='p6-card__stage p6-games'>
              <div className='p6-games__selected'>
                <img className='is-active' src={TRIAL_BETA_ASSETS.coverCs2} alt={t('howItWorks.games.cs2')} data-game-cover='0' />
                <img src={TRIAL_BETA_ASSETS.coverValorant} alt={t('howItWorks.games.valorant')} data-game-cover='1' />
                <img src={TRIAL_BETA_ASSETS.coverDota2} alt={t('howItWorks.games.dota2')} data-game-cover='2' />
                <img src={TRIAL_BETA_ASSETS.coverFortnite} alt={t('howItWorks.games.fortnite')} data-game-cover='3' />
                <strong data-game-name>{t('howItWorks.games.defaultName')}</strong>
              </div>
              <div className='p6-games__options' aria-label={t('howItWorks.games.optionsAriaLabel')}>
                <button className='is-active' type='button' data-game-button='0' aria-label={t('howItWorks.games.selectCs2')} aria-pressed='true'>
                  <img src={TRIAL_BETA_ASSETS.coverCs2} alt='' />
                </button>
                <button type='button' data-game-button='1' aria-label={t('howItWorks.games.selectValorant')} aria-pressed='false'>
                  <img src={TRIAL_BETA_ASSETS.coverValorant} alt='' />
                </button>
                <button type='button' data-game-button='2' aria-label={t('howItWorks.games.selectDota2')} aria-pressed='false'>
                  <img src={TRIAL_BETA_ASSETS.coverDota2} alt='' />
                </button>
                <button type='button' data-game-button='3' aria-label={t('howItWorks.games.selectFortnite')} aria-pressed='false'>
                  <img src={TRIAL_BETA_ASSETS.coverFortnite} alt='' />
                </button>
              </div>
            </div>
            <h2>{t('howItWorks.games.title')}</h2>
            <p>{t('howItWorks.games.description')}</p>
            <div className='p6-card__status'>
              <i />
              <span data-game-status>{t('howItWorks.games.defaultStatus')}</span>
            </div>
          </article>

          <div className='p6-connector' aria-hidden='true'>
            <i />
            <b />
            <i />
          </div>

          <article className='p6-card p6-card--performance' data-step='performance'>
            <div className='p6-card__number'>
              <b>{t('howItWorks.performance.number')}</b>
              <span>{t('howItWorks.performance.label')}</span>
            </div>
            <div className='p6-card__stage p6-performance is-offline'>
              <div className='p6-performance__top'>
                <span>
                  <i /> {t('howItWorks.performance.liveConnection')}
                </span>
                <b data-connection-state>{t('howItWorks.performance.defaultConnectionState')}</b>
              </div>
              <div className='p6-ping-gauge'>
                <div className='p6-ping-gauge__core'>
                  <b data-ping-value>--</b>
                  <span>{t('howItWorks.performance.pingUnit')}</span>
                  <small>{t('howItWorks.performance.pingLabel')}</small>
                </div>
                <i className='p6-ping-gauge__orbit' />
              </div>
              <div className='p6-performance__bottom'>
                <div className='p6-fps-output'>
                  <span>{t('howItWorks.performance.fpsLabel')}</span>
                  <div>
                    <b data-fps-value>84</b>
                    <em>{t('howItWorks.performance.fpsUnit')}</em>
                    <i data-fps-gain>+0%</i>
                  </div>
                  <div className='p6-fps-bars' aria-hidden='true'>
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                  </div>
                </div>
                <div className='p6-quality'>
                  <div>
                    <span>{t('howItWorks.performance.jitterLabel')}</span>
                    <b data-jitter-value>--</b>
                    <em>{t('howItWorks.performance.jitterUnit')}</em>
                  </div>
                  <div>
                    <span>{t('howItWorks.performance.lossLabel')}</span>
                    <b data-loss-value>--</b>
                    <em>{t('howItWorks.performance.lossUnit')}</em>
                  </div>
                </div>
              </div>
            </div>
            <h2>{t('howItWorks.performance.title')}</h2>
            <p>{t('howItWorks.performance.description')}</p>
            <div className='p6-card__status'>
              <i />
              <span data-performance-status>{t('howItWorks.performance.defaultStatus')}</span>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
